import _ from "lodash";
import * as UtilsGlobals from "./Globals";
import { saveAs } from "file-saver";
import { isDefined, isFunction, IsNullOrEmpty, IsNullOrWhiteSpace } from "./Micro";
// ------------------------------------------------------
// Build time parameters
declare var __IN_DEBUG__: boolean;
declare var __VERSION__: string;
declare var __GIT_VERSION__: string;
declare var process: any;
declare var __TIMESTAMP__: string;
// ------------------------------------------------------
export function isEqualsSafe(primeOrig: any, secOrig: any, skipElemets?: string[], skipFunctions?: boolean): boolean {
    var funcTest: (prime: any, sec: any, depth: number) => boolean = (prime, sec, depth) => {
        if (prime === sec)
            return true;
        // if both x and y are null or undefined and exactly the same

        if (!(prime instanceof Object) || !(sec instanceof Object))
            return false;
        // if they are not strictly equal, they both need to be Objects

        if (prime.constructor !== sec.constructor)
            return false;
        // they must have the exact same prototype chain, the closest we can do is
        // test there constructor.

        for (var p in prime) {
            if (!_.has(prime, p))
                continue;
            // other properties were tested using x.constructor === y.constructor

            if (isDefined(skipElemets) && skipElemets.findIndex((value) => value === p) !== -1)
                continue;
            // TODO: better handling of skip elements with more local testing;

            if (isDefined(skipFunctions) && skipElemets && isFunction(prime[p]))
                continue;
            // skip functions from equality

            if (!_.has(sec, p))
                return false;
            // allows to compare x[ p ] and y[ p ] when set to undefined

            if (prime[p] === sec[p])
                continue;
            // if they have the same strict value or identity then they are equal

            if (typeof (prime[p]) !== "object")
                return false;
            // Numbers, Strings, export  functions, Booleans must be strictly equal

            // We check up to 10 layers of objects
            if (depth > 10)
                return true;

            // Objects and Arrays must be tested recursively
            if (!funcTest(prime[p], sec[p], depth + 1))
                return false;
        }

        for (p in sec) {
            if (_.has(sec, p) && !_.has(prime, p))
                return false;
            // allows sec[ p ] to be set to undefined
        }
        return true;
    }
    return funcTest(primeOrig, secOrig, 0);
}
// ------------------------------------------------------
export function stripIndent(str: string) {
    const match = str.match(/^[ \t]*(?=\S)/gm);

    if (!match) {
        return str;
    }

    const indent = Math.min.apply(Math, match.map(x => x.length)); // eslint-disable-line
    const re = new RegExp(`^[ \\t]{${indent}}`, 'gm');

    return indent > 0 ? str.replace(re, '') : str;
}
// ------------------------------------------------------
export function parseStrIntoStrArr(str: string): string[] {
    // Is incoming string empty?
    if (_.isEmpty(str))
        return [];
    // Or maybe already an array?
    else if (_.isArray(str))
        return str;

    // If parsable, parse it into array of strings.
    if ((_.startsWith(str, '[') && _.endsWith(str, ']')) ||
        (_.startsWith(str, '{') && _.endsWith(str, '}'))) {
        try {
            const arr = JSON.parse(str);
            if (_.isArray(arr))
                return arr;
        }
        catch (_) { return []; }
    }
    // got just a string; return array
    return [str];
}
// ------------------------------------------------------
export function getAppInfo(): { [key: string]: any } {
    let appName = UtilsGlobals.getGlobal("__APP_NAME__");
    let params = UtilsGlobals.getGlobal("__APP_PARAMS__");
    return {
        "__IN_DEBUG__": __IN_DEBUG__,
        "__VERSION__": __VERSION__,
        "__GIT_VERSION__": __GIT_VERSION__,
        "__TIMESTAMP__": __TIMESTAMP__,
        "NODE_ENV": process.env.NODE_ENV,
        "__APP_NAME__": isDefined(appName) ? appName : "",
        "__APP_PARAMS__": isDefined(params) ? params : "",
    };
}
// ------------------------------------------------------
export function inDebug(): boolean {
    return __IN_DEBUG__;
}
// ------------------------------------------------------
// Fullscreen
// ------------------------------------------------------
var fn: any = (function () {
    var val;
    var valLength;

    var fnMap = [
        [
            'requestFullscreen',
            'exitFullscreen',
            'fullscreenElement',
            'fullscreenEnabled',
            'fullscreenchange',
            'fullscreenerror'
        ],
        // new WebKit
        [
            'webkitRequestFullscreen',
            'webkitExitFullscreen',
            'webkitFullscreenElement',
            'webkitFullscreenEnabled',
            'webkitfullscreenchange',
            'webkitfullscreenerror'

        ],
        // old WebKit (Safari 5.1)
        [
            'webkitRequestFullScreen',
            'webkitCancelFullScreen',
            'webkitCurrentFullScreenElement',
            'webkitCancelFullScreen',
            'webkitfullscreenchange',
            'webkitfullscreenerror'

        ],
        [
            'mozRequestFullScreen',
            'mozCancelFullScreen',
            'mozFullScreenElement',
            'mozFullScreenEnabled',
            'mozfullscreenchange',
            'mozfullscreenerror'
        ],
        [
            'msRequestFullscreen',
            'msExitFullscreen',
            'msFullscreenElement',
            'msFullscreenEnabled',
            'MSFullscreenChange',
            'MSFullscreenError'
        ]
    ];

    var i = 0;
    var l = fnMap.length;
    var ret = {};

    for (; i < l; i++) {
        val = fnMap[i];
        if (val && val[1] in document) {
            for (i = 0, valLength = val.length; i < valLength; i++) {
                ret[fnMap[0][i]] = val[i];
            }
            return ret;
        }
    }
    return false;
})();
// ------------------------------------------------------
export function fullscreenRequest(elem?: HTMLElement) {
    var request = fn.requestFullscreen;
    elem = elem || document.documentElement;

    // Work around Safari 5.1 bug: reports support for
    // keyboard in fullscreen even though it doesn't.
    // Browser sniffing, since the alternative with
    // setTimeout is even worse.
    if (/5\.1[\.\d]* Safari/.test(navigator.userAgent)) {
        elem[request]();
    } else if (/webkit/.test(request)) {
        var keyboardAllowed = typeof Element !== 'undefined' && 'ALLOW_KEYBOARD_INPUT' in Element;
        elem[request](keyboardAllowed && (Element as any).ALLOW_KEYBOARD_INPUT);
    } else {
        elem[request]({ navigationUI: "auto" });
    }
}
// ------------------------------------------------------
export function fullscreenExit() {
    document[fn.exitFullscreen]();
}
// ------------------------------------------------------
export function fullscreenToggle(elem?: HTMLElement) {
    if (isFullscreen()) {
        fullscreenExit();
    } else {
        fullscreenRequest(elem);
    }
}
// ------------------------------------------------------
export function isFullscreen(): boolean {
    return Boolean(document[fn.fullscreenElement]);
}
// ------------------------------------------------------
export function screenWidth(): number {
    return window.innerWidth
        || document.documentElement.clientWidth
        || document.body.clientWidth;
}
// ------------------------------------------------------
export function screenHeight(): number {
    return window.innerHeight
        || document.documentElement.clientHeight
        || document.body.clientHeight;
}
// ------------------------------------------------------
// Popup window
// ------------------------------------------------------
const popupStaticWindowId = "__POPUP_STATIC_WINDOW__";
let _popupStaticWindow: (path: string, targetBlank?: boolean) => void = null;
export function popupStaticWindow(path: string, targetBlank: boolean = false) {
    // set global if not defined
    if (!isDefined(_popupStaticWindow)) {
        UtilsGlobals.setGlobal(popupStaticWindowId, (path_: string, targetBlank_: boolean) => {
            if (targetBlank_)
                window.open(path_, path_).focus();
            else
                window.open(`${window.location.origin}/${path_}`, path_).focus();

        });
        _popupStaticWindow = UtilsGlobals.getGlobal(popupStaticWindowId);
    }

    if (!IsNullOrEmpty(path) && !IsNullOrWhiteSpace(path))
        _popupStaticWindow(path, targetBlank);
}
// ------------------------------------------------------
// GUID
// ------------------------------------------------------
export function getRandom128bGenerator(): () => Uint8Array {
    // Unique ID creation requires a high quality random # generator.  In the
    // browser this is a little complicated due to unknown quality of Math.random()
    // and inconsistent support for the `crypto` API.  We do the best we can via
    // feature-detection
    var rng: () => Uint8Array;

    var crypto = window.crypto || (window as any).msCrypto; // for IE 11
    if (crypto && crypto.getRandomValues) {
        // WHATWG crypto RNG - http://wiki.whatwg.org/wiki/Crypto
        var rnds8 = new Uint8Array(16);
        rng = function whatwgRNG() {
            crypto.getRandomValues(rnds8);
            return rnds8;
        };
    }

    if (!rng) {
        // Math.random()-based (RNG)
        //
        // If all else fails, use Math.random().  It's fast, but is of unspecified
        // quality.
        var rnds = new Uint8Array(16);
        rng = function () {
            for (var i = 0, r; i < 16; i++) {
                if ((i & 0x03) === 0) r = Math.random() * 0x100000000;
                rnds[i] = r >>> ((i & 0x03) << 3) & 0xff;
            }
            return rnds;
        };
    }
    return rng;
}
// ------------------------------------------------------

// **`v1()` - Generate time-based UUID**
//
// Inspired by https://github.com/LiosK/UUID.js
// and http://docs.python.org/library/uuid.html

// random #'s we need to init node and clockseq
var _seedBytes = getRandom128bGenerator()();

// Per 4.5, create and 48-bit node id, (47 random bits + multicast bit = 1)
var _nodeId = [
    _seedBytes[0] | 0x01,
    _seedBytes[1], _seedBytes[2], _seedBytes[3], _seedBytes[4], _seedBytes[5]
];

// Per 4.2.2, randomize (14 bit) clockseq
var _clockseq = (_seedBytes[6] << 8 | _seedBytes[7]) & 0x3fff;

// Previous uuid creation time
var _lastMSecs = 0, _lastNSecs = 0;

// mapping of bytes to hex
var byteToHex = [];
for (var i = 0; i < 256; ++i) {
    byteToHex[i] = (i + 0x100).toString(16).substr(1);
}
// ------------------------------------------------------
export function getGuid(): string {
    // See https://github.com/broofa/node-uuid for API details
    var i = 0;
    var b = [];
    var clockseq = _clockseq;

    // UUID timestamps are 100 nano-second units since the Gregorian epoch,
    // (1582-10-15 00:00).  JSNumbers aren't precise enough for this, so
    // time is handled internally as 'msecs' (integer milliseconds) and 'nsecs'
    // (100-nanoseconds offset from msecs) since unix epoch, 1970-01-01 00:00.
    var msecs = new Date().getTime();

    // Per 4.2.1.2, use count of uuid's generated during the current clock
    // cycle to simulate higher resolution clock
    var nsecs = _lastNSecs + 1;

    // Time since last uuid creation (in msecs)
    var dt = (msecs - _lastMSecs) + (nsecs - _lastNSecs) / 10000;

    // Per 4.2.1.2, Bump clockseq on clock regression
    if (dt < 1) {
        clockseq = clockseq + 1 & 0x3fff;
    }

    // Reset nsecs if clock regresses (new clockseq) or we've moved onto a new
    // time interval
    if (dt < 0 || msecs > _lastMSecs) {
        nsecs = 0;
    }

    // Per 4.2.1.2 Throw error if too many uuids are requested
    if (nsecs >= 10000) {
        throw new Error('uuid.v1(): Can\'t create more than 10M uuids/sec');
    }

    _lastMSecs = msecs;
    _lastNSecs = nsecs;
    _clockseq = clockseq;

    // Per 4.1.4 - Convert from unix epoch to Gregorian epoch
    msecs += 12219292800000;

    // `time_low`
    var tl = ((msecs & 0xfffffff) * 10000 + nsecs) % 0x100000000;
    b[i++] = tl >>> 24 & 0xff;
    b[i++] = tl >>> 16 & 0xff;
    b[i++] = tl >>> 8 & 0xff;
    b[i++] = tl & 0xff;

    // `time_mid`
    var tmh = (msecs / 0x100000000 * 10000) & 0xfffffff;
    b[i++] = tmh >>> 8 & 0xff;
    b[i++] = tmh & 0xff;

    // `time_high_and_version`
    b[i++] = tmh >>> 24 & 0xf | 0x10; // include version
    b[i++] = tmh >>> 16 & 0xff;

    // `clock_seq_hi_and_reserved` (Per 4.2.2 - include variant)
    b[i++] = clockseq >>> 8 | 0x80;

    // `clock_seq_low`
    b[i++] = clockseq & 0xff;

    // `node`
    var node = _nodeId;
    for (var n = 0; n < 6; ++n) {
        b[i + n] = node[n];
    }

    // return a string presentation
    i = 0;
    return byteToHex[b[i++]] + byteToHex[b[i++]] +
        byteToHex[b[i++]] + byteToHex[b[i++]] + '-' +
        byteToHex[b[i++]] + byteToHex[b[i++]] + '-' +
        byteToHex[b[i++]] + byteToHex[b[i++]] + '-' +
        byteToHex[b[i++]] + byteToHex[b[i++]] + '-' +
        byteToHex[b[i++]] + byteToHex[b[i++]] +
        byteToHex[b[i++]] + byteToHex[b[i++]] +
        byteToHex[b[i++]] + byteToHex[b[i++]];
}
// ------------------------------------------------------
// Download API
// ------------------------------------------------------
export function DownloadImage(base64: string, fileName: string): void {
    let data = base64;

    var img = document.createElement('img');
    img.src = data;

    var element = document.createElement('a');
    element.setAttribute("download", fileName);
    element.setAttribute("href", data);
    element.appendChild(img);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();
    document.body.removeChild(element);
}
// ------------------------------------------------------
// Copy-To-Clipboard
// ------------------------------------------------------
export function ToggleSelection() {
    let selection = document.getSelection();
    if (!selection.rangeCount) {
        return function () { };
    }
    let active: Element = document.activeElement;

    var ranges = [];
    for (var i = 0; i < selection.rangeCount; i++) {
        ranges.push(selection.getRangeAt(i));
    }

    switch (active.tagName.toUpperCase()) { // .toUpperCase handles XHTML
        case 'INPUT':
        case 'TEXTAREA':
            (active as any).blur();
            break;

        default:
            active = null;
            break;
    }

    selection.removeAllRanges();
    return function () {
        selection.type === 'Caret' && selection.removeAllRanges();
        if (!selection.rangeCount) {
            ranges.forEach(function (range) {
                selection.addRange(range);
            });
        }

        active && (active as any).focus();
    };
}
// ------------------------------------------------------
export function CopyToClipboard(text: string, options?: any) {
    function format(message) {
        var copyKey = (/mac os x/i.test(navigator.userAgent) ? '⌘' : 'Ctrl') + '+C';
        return message.replace(/#{\s*key\s*}/g, copyKey);
    }
    var defaultMessage = 'Copy to clipboard: #{key}, Enter';

    var debug, message, reselectPrevious, range, selection, mark, success = false;
    if (!options) { options = {}; }
    debug = options.debug || false;
    try {
        reselectPrevious = ToggleSelection();

        range = document.createRange();
        selection = document.getSelection();

        mark = document.createElement('span');
        mark.textContent = text;
        mark.setAttribute('style', [
            // reset user styles for span element
            'all: unset',
            // prevents scrolling to the end of the page
            'position: fixed',
            'top: 0',
            'clip: rect(0, 0, 0, 0)',
            // used to preserve spaces and line breaks
            'white-space: pre',
            // do not inherit user-select (it may be `none`)
            '-webkit-user-select: text',
            '-moz-user-select: text',
            '-ms-user-select: text',
            'user-select: text',
        ].join(';'));

        document.body.appendChild(mark);

        range.selectNode(mark);
        selection.addRange(range);

        var successful = document.execCommand('copy');
        if (!successful) {
            throw new Error('copy command was unsuccessful');
        }
        success = true;
    } catch (err) {
        debug && console.error('unable to copy using execCommand: ', err);
        debug && console.warn('trying IE specific stuff');
        try {
            (window as any).clipboardData.setData('text', text);
            success = true;
        } catch (err) {
            debug && console.error('unable to copy using clipboardData: ', err);
            debug && console.error('falling back to prompt');
            message = format('message' in options ? options.message : defaultMessage);
            window.prompt(message, text);
        }
    } finally {
        if (selection) {
            if (typeof selection.removeRange == 'function') {
                selection.removeRange(range);
            } else {
                selection.removeAllRanges();
            }
        }

        if (mark) {
            document.body.removeChild(mark);
        }
        reselectPrevious();
    }
    return success;
}
// ------------------------------------------------------
var focusFuncList: { func: (vissible: boolean, priv: any) => void, priv?: any, id: number }[] = [];
var focusIdCount = 0;
var focusStarted = false;
var hidden = "hidden";
// ------------------------------------------------------
function onFocusChange(evt) {
    var v = true, h = false, evtMap = {
        focus: v, focusin: v, pageshow: v, blur: h, focusout: h, pagehide: h
    };

    evt = evt || window.event;
    let isVisible = true;
    if (evt.type in evtMap) {
        isVisible = evtMap[evt.type];
    } else {
        isVisible = document[hidden] ? false : true;
    }

    focusFuncList.forEach((f) => {
        if (isFunction(f.func))
            f.func(isVisible, f.priv);
    });
}
// ------------------------------------------------------
export function addDocumentFocus(func: (vissible: boolean, priv: any) => void, priv: any = null): number {
    let id = focusIdCount++;
    focusFuncList.push({ func, priv, id });

    // Register the fist time
    if (!focusStarted) {
        focusStarted = true;

        // Standards:
        if (hidden in document)
            document.addEventListener("visibilitychange", onFocusChange);
        else if ((hidden = "mozHidden") in document)
            document.addEventListener("mozvisibilitychange", onFocusChange);
        else if ((hidden = "webkitHidden") in document)
            document.addEventListener("webkitvisibilitychange", onFocusChange);
        else if ((hidden = "msHidden") in document)
            document.addEventListener("msvisibilitychange", onFocusChange);
        // All others:
        else
            window.onpageshow = window.onpagehide = window.onfocus = window.onblur = onFocusChange;

        // set the initial state (but only if browser supports the Page Visibility API)
        if (document[hidden] !== undefined)
            onFocusChange({ type: document[hidden] ? "blur" : "focus" });
    }

    return id;
}
// ------------------------------------------------------
export function removeDocumentFocus(id: number) {
    let i = focusFuncList.findIndex(x => x.id == id);
    if (i > -1) {
        focusFuncList.splice(i, 1);
    }
}
// ------------------------------------------------------
// CSV helper
// ------------------------------------------------------
export function downloadCSV(data: string[][], filename: string) {
    let csvContent = "";
    _.forEach(data, function (rowArray) {
        let row = rowArray.join(",");
        csvContent += row + "\r\n";
    });
    saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8" }), filename);
}
// ------------------------------------------------------
export function dataURItoBlob(dataURI) {
    if (!isDefined(dataURI))
        return null;

    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    var byteString = atob(dataURI.split(',')[1]);

    // separate out the mime component
    var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    var ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    var blob = new Blob([ab], { type: mimeString });
    return blob;
}
// ------------------------------------------------------
// lodash extensions
// ------------------------------------------------------
export function reduceMapObject<T extends object, TResult>(
    collection: T,
    func: _.ObjectIterator<T, TResult>
): TResult[] {
    return _.reduce(collection,
        (acc, obj, index) => {
            let res = func(obj, index, collection);
            if (res != undefined)
                acc.push(res);
            return acc;
        },
        []);
}
// ------------------------------------------------------
export function urlEncode(object: { [key: string]: any }): string {
    return Object
        .keys(object)
        .map(key => {
            let value = object[key];
            if (_.isObject(value))
                value = JSON.stringify(value);
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');
}
// ------------------------------------------------------
export function arrayMoveMutable(array: any[], fromIndex: number, toIndex: number) {
    if (_.isArray(array)) {
        let startIndex = fromIndex < 0 ? array.length + fromIndex : fromIndex;
        if (startIndex >= 0 && startIndex < array.length) {
            let endIndex = toIndex < 0 ? array.length + toIndex : toIndex;
            let [item] = array.splice(fromIndex, 1);
            array.splice(endIndex, 0, item);
        }
    }
}
// ------------------------------------------------------
export function arrayMoveImmutable(array: any[], fromIndex: number, toIndex: number) {
    if (_.isArray(array)) {
        array = [...array];
        arrayMoveMutable(array, fromIndex, toIndex);
        return array;
    }
}
// ------------------------------------------------------
// Escape characters with special meaning either inside or outside character sets.
// Use a simple backslash escape when it’s always valid, and a `\xnn` escape when 
// the simpler form would be disallowed by Unicode patterns’ stricter grammar.
export function escapeStringRegExp(string: string) {
    if (_.isString(string)) {
        return string
            .replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')
            .replace(/-/g, '\\x2d');
    }
}
// ------------------------------------------------------
export function safeStringify(object: any): string {
    const getCircularReplacer = () => {
        const seen = new WeakSet();
        return (key, value) => {
            if (typeof value === "object" && value !== null) {
                if (seen.has(value)) {
                    return;
                }
                seen.add(value);
            }
            return value;
        };
    };

    return JSON.stringify(object, getCircularReplacer());
}
// ------------------------------------------------------
export function yHas(object: any, path: string | number): boolean {
    return _.has(object, path);
}
// ------------------------------------------------------